/**
 * Hari created on 01/02/2018 for LLC optimization project
 */
import { Injectable } from '@angular/core';
import { LLC } from '../../models/questionnaire-model';
import { LLCMapped, FieldAnswer, GroupFieldAnswer, GroupAnswer } from '../../models/llcmapped-model';
import { ProductConfigurationIds } from '../../models/cart-model';
import { Contact, ContactType } from '../../models/address-model';
import { ConstantsService } from '../constants.service';
import { RegisteredAgentType } from '../../models/registered-agent.model';
import { ExperimentsService } from '../experiments/experiments.service';
import { IUserAnswerResponse } from '@legalzoom/questionnaire-answer-sdk';

// Constants start
/**
 * Represents a CHECKED value
 */
enum MAPPING_CHECK {
  'checked' = '1',
  'unchecked' = '2',
}
enum MAPPING_YESNO {
  'Yes' = 'Yes',
  'No' = 'No',
}

enum MAPPING_REGISTERED_AGENT {
  'Legalzoom' = 'LegalZoom will serve as my registered agent.',
  'Self' = 'I will name the registered agent.'
}

enum VALUES_REGISTERED_AGENT {
  'Legalzoom' = 'Legalzoom',
  'Self' = 'Self'
}

enum MAPPING_NULL {
  'NULL' = 'null',
  'NEGATIVEONE' = '-1',
}

enum MAPPING_EIN_CHOICE {
  'Yes' = 'Yes, please obtain my tax identification number for me.',
  'YesForm' = 'Yes, please prepare the IRS form only.',
  'No' = 'No'
}

enum VALUES_EIN_CHOICE {
  'Yes' = 'Yes',
  'YesForm' = 'YesForm',
  'No' = 'No'
}

enum VALUES_TRADE_NAME {
  'Yes' = 'Yes',
  'NotSure' = 'NotSure',
  'No' = 'No'
}

enum MAPPING_BAS_OFFER {
  'Yes' = 'Yes, include my free forms and enroll me in Business Advantage',
  'No' = 'No, I decline this offer'
}

enum MAPPING_BOFA_OFFER {
  'Yes' = 'Yes, please have a Small Business Specialist contact me',
  'No' = 'No, I am not interested at this time'
}

enum MAPPING_ADP_OFFER {
  'Yes' = 'Yes, please have an ADP representative contact me directly',
  'No' = 'No, I\'m not interested at this time'
}

enum MAPPING_NOOFOWNERS {
  'one' = '1 owner',
  'two' = '2 owners',
  'three' = '3 owners'
}

enum VALUES_NOOFOWNERS {
  'one' = 'one',
  'two' = 'two',
  'three' = 'three'
}


enum MAPPING_NEWBUSINESS {
  'already' = 'already in business',
  'soon' = 'soon',
  'future' = 'future'
}

enum VALUES_NEWBUSINESS {
  'already' = 'already',
  'soon' = 'soon',
  'future' = 'future'
}

enum MAPPING_SELECTTED_PKG {
  'Economy' = 'Economy',
  'Standard' = 'Standard',
  'Gold' = 'Gold'
}

enum VALUES_MANAGEDOWNER {
  'solelyByOwner' = 'solelyByOwner',
  'ownersAndNonOwners' = 'ownersAndNonOwners',
  'nonOwnersOnly' = 'nonOwnersOnly'
}

enum MAPPING_MANAGEDOWNER {
  'solelyByOwner' = 'solely by the owner (Member-managed)',
  'ownersAndNonOwners' = 'by the owner and other managers (Manager-managed)',
  'nonOwnersOnly' = 'by other managers only (Manager-managed)'
}

enum MAPPING_SINGLEOWNER_MANAGEMENTLLC { // this is single field for both 1 owner and multiple owners
  'solelyByOwner' = 'By its single member.',
  'ownersAndNonOwners' = 'By more than one manager.',
  'nonOwnersOnly' = 'By more than one manager.'
}

enum VALUES_MANAGEDOWNERS {
  'allOwners' = 'allOwners',
  'someOwners' = 'someOwners',
  'ownersAndNonOwners' = 'ownersAndNonOwners',
  'nonOwnersOnly' = 'nonOwnersOnly'
}

enum MAPPING_MANAGEDOWNERS {
  'allOwners' = 'by all owners (Member-managed)',
  'someOwners' = 'by some but not all owners (Manager-managed)',
  'ownersAndNonOwners' = 'by both owners and other managers (Manager-managed)',
  'nonOwnersOnly' = 'by other managers only (Manager-managed)'
}

enum MAPPING_MULTIPLEOWNERS_MANAGEMENTLLC { // this is single field for both 1 owner and multiple owners
  'allOwners' = 'By all of the members.',
  'someOwners' = 'By more than one manager.',
  'ownersAndNonOwners' = 'By more than one manager.',
  'nonOwnersOnly' = 'By more than one manager.'
}

enum MAPPING_CTNCONSULT {
  'Yes' = 'Yes, I would like to learn about reducing my tax expenses',
  'No' = 'No thanks'
}

enum MAPPING_CEPCONSULT {
  'Yes' = 'Yes, I would like to learn about estate planning',
  'No' = 'No thanks'
}

enum VALUES_COMPLIANCEPKG {
  'fullservice' = 'fullservice',
  'basic' = 'basic',
  'nothanks' = 'nothanks'
}

enum MAPPING_ADVISORYOFFER {
  'Yes' = 'Yes, I want year-around legal advice ( $336/year*) Save 30% today with your LLC',
  'No' = 'No, thanks'
}

enum MAPPING_BOAOFFER {
  'Yes' = 'Yes, please have a Small Business Specialist contact me',
  'No' = 'No, I am not interested at this time'
}

enum MAPPING_WEBCOMOFFER {
  'Yes' = 'Yes, I\'m interested',
  'No' = 'No thanks'
}

enum MAPPING_OA_BUNDLE {
  'OA_Only' = 'OA Only',
  'OA_And_EIN' = 'OA And EIN',
  'OA_And_EIN_And_BL' = 'OA And EIN And BL'
}

enum MAPPING_CONSENT {
  'allOwnersConsent' = 'The membership must vote unanimously.',
  'majorityConsent' = 'A majority of the other members must consent.',
  'noConsent' = 'Ownership interests are freely transferable.'
}

enum VALUES_CONSENT {
  'allOwnersConsent' = 'allOwnersConsent',
  'majorityConsent' = 'majorityConsent',
  'noConsent' = 'noConsent'
}

// Constants end

@Injectable()
export class QuestionnaireMappingService {
  // tslint:disable-next-line: variable-name
  public _mapped: LLCMapped;
  public questionnaireMappingResponse: IUserAnswerResponse;
  // tslint:disable-next-line: variable-name
  public _fieldAnswerLookup: Array<string>;
  // tslint:disable-next-line: variable-name
  public _groupAnswerCollectionLookup: Array<string>;
  // tslint:disable-next-line: variable-name
  public _groupFieldAnswerLookup: Array<string>;
  private groupAnswerKeySet: Set<string>;
  private groupAnswerArray: GroupAnswer[];
  managersIndex = 0;

  constructor(
    private experimentsService: ExperimentsService
  ) {
    this.initialize();
  }

  initialize() {
    this._mapped = new LLCMapped();
    this._fieldAnswerLookup = new Array<string>();
    this.groupAnswerKeySet = new Set<string>();
    this.groupAnswerArray = [];
    this._groupFieldAnswerLookup = new Array<string>();
  }


  /**
   * Sets the FieldAnswer value
   * @param key - The Field Name
   * @param value - The value to assign to the Answer
   */
  public setFieldAnswer(key: string, value: string) {
    try {
      if (key == null || key === 'undefined') {
        return;
      }

      const i = this._fieldAnswerLookup.indexOf(key);
      if (i === -1) {
        // If key doesn't exist
        const fa = { fieldName: '', fieldValue: '' } as FieldAnswer;
        fa.fieldName = key;
        fa.fieldValue = ((value === undefined || value === null) ? '' : value);

        // Add to Mapped
        this._mapped.fieldAnswers.push(fa);

        // Add to Lookoup
        this._fieldAnswerLookup.push(key);
      } else if (this._mapped.fieldAnswers[i].fieldValue === '' && value === undefined) {
        return;
      } else {
        // Key already exists.  Just set value.
        this._mapped.fieldAnswers[i].fieldValue = value;
        // o.value = value;
      }
    } catch (e) {

    }
  }

  /**
   * Gets the FieldAnswer value
   * @param key - The Field Name
   */

  public getFieldAnswer(key: string): FieldAnswer {
    try {
      if (key == null || key === 'undefined') {
        return;
      }
      const fieldObj = this._mapped.fieldAnswers.find(x => x.fieldName === key);
      return fieldObj;
    } catch (e) { }
  }

  public setGroupAnswer(groupName: string, key: string, value: string): void {
    if (!groupName || !key) {
      return;
    }

    // Check if group collection exists
    if (!this.groupAnswerKeySet.has(key)) {
      const ga: GroupAnswer = {
        groupName,
        fieldName: key,
        fieldValue: (value === undefined) ? '' : value
      };

      // Add empty group collection to mapping
      this.groupAnswerArray.push(ga);

      // Add group key to lookup set
      this.groupAnswerKeySet.add(key);
    }
  }
  /**
   * Sets the GroupFieldAnswer value by Group Name
   * @param groupName - The Group Name
   * @param key - The Cell Name
   * @param value - The Cell Value
   */

  /**
   * Gets the states availabe for the RA FQ Questions
   * @param state - The full name of the state
   */
  private isStateFQQuestion(state: string) {
    if (state === 'Arizona' || state === 'Rhode Island'
      || state === 'Texas' || state === 'Utah' || state === 'Virginia') {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Gets the states available for the SelfOrLZ question
   * @param state - The full name of the state
   */
  private isStateRAQuestion(state) {
    if (state === 'Arkansas' || state === 'Arizona' || state === 'District of Columbia'
      || state === 'Minnesota' || state === 'Pennsylvania' || state === 'Rhode Island'
      || state === 'Texas' || state === 'Utah' || state === 'Virginia'
      || state === 'Vermont') {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Gets the states availabe for the RA FQ Questions
   * @param state - The full name of the state
   */


  /**
   * Checks if the object is NULL or 'undefined'
   * @param obj - The object to check
   */
  private isUndefined(obj: any) {
    return (obj == null || typeof obj === 'undefined');
  }

  private isNullOrEmpty(obj: any) {
    // tslint:disable-next-line: triple-equals
    return (this.isUndefined(obj) && obj == '');
  }

  private getReplaceSaint(original) {
    if (original == null || original === 'undefined') { return original; }

    return original.replace('Saint', 'St.');
  }

  /**
   * Converts date (containing year, year and month, or year and month and day) to a three part date in USA format
   */
  private updateDateValueToCorrectFormat(date) {
    if (!date) { return ''; }
    date = date.toString().trim();
    if (date.toLowerCase() === 'upon filing') { return 'Upon Filing'; }

    let parts = date.replace(/[.,\/ \-]/g, ' ').replace(/\s+/g, ' ').split(' '); // sanitize string and split
    if (parts.length > 3 || date.replace(/[0-9.,\/ \-]/g, '').length !== 0) {
      return ''; // date has too many parts, or contains invalid characters
    }

    // derive three part date and set date ordering to that of international format, YYYY-MM-DD
    if (parts.length < 2) { // single part date, add month and day
      if (parts[0].length < 4) { return ''; } // no year provided
      parts = [parts[0], '01', '01'];
    } else if (parts.length < 3) { // two part date, assume month & year, add day
      if (parts[0].length === 4) { // first item is year, YYYY-MM
        parts = [parts[0], parts[1], '01'];
      } else if (parts[1].length === 4) { // second item is year, MM/YYYY
        parts = [parts[1], parts[0], '01'];
      } else { return ''; } // no year provided
    } else if (parts[0].length !== 4) { // three part date not starting with year, ! YYYY-MM-DD
      if (date.indexOf('/') > 0 && parts[2].length === 4) { // USA style separator ending with year, assuming MM/DD/YYYY
        parts = [parts[2], parts[0], parts[1]];
      } else if (parts[2].length === 4) { // other format ending with year, assuming DD*MM*YYYY
        parts = [parts[2], parts[1], parts[0]];
      } else { return ''; } // no year provided or unknown date format
    } // else, assuming date provided is in the correct format since first value is 4 characters long and has three parts

    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (m < 1 || m > 12 || d < 1 || d > 31) { return ''; } // month or day are out of reasonable value range

    // ensure leading zeros
    parts[1] = ('0' + parts[1]).slice(-2);
    parts[2] = ('0' + parts[2]).slice(-2);

    return parts[1] + '/' + parts[2] + '/' + parts[0]; // USA date format
    // return parts.join('-'); // international date format
  }

  /**
   * Fixes the phone format to be (XXX) XXX-XXXX
   */
  private updatePhoneFormat(phone) {
    if (!phone) { return ''; }
    if (phone.length < 1) { return ''; }

    return '(' + phone.slice(0, 3) + ') ' + phone.slice(3, 6) + '-' + phone.slice(6);
  }

  /**
   * Fixes the SSN format to be XXX-XX-XXXX
   */
  private updateSsnFormat(ssn) {
    if (!ssn) { return ssn; }
    // tslint:disable-next-line: triple-equals
    if (ssn.length != 9) { return ssn; }
    return ssn.slice(0, 3) + '-' + ssn.slice(3, 5) + '-' + ssn.slice(5);
  }

  /**
   * Performs the mapping from the LLC Object to the Key/Value Pairs
   */
  public doMapping(llcData: LLC, userOrderId: number) {

    // tslint:disable-next-line: variable-name
    const _llc = llcData;
    this.initialize();
    this._mapped.userOrderId = userOrderId;
    let s = _llc.entityState;

    // Return if empty object
    if (!_llc) { return; }
    if (this._mapped.userOrderId === 0) { return; }
    // sState - Entity state
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.entityState)) {
      this.setFieldAnswer('sState', _llc.entityState);
    }

    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.isLZStateTaxId)) {
      if (_llc.isLZStateTaxId) {
        this.setFieldAnswer('bLZStateTaxId', MAPPING_YESNO.Yes);
      } else {
        this.setFieldAnswer('bLZStateTaxId', MAPPING_YESNO.No);
      }
    } else {
      this.setFieldAnswer('bLZStateTaxId', '');
    }

    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.isLZSellersPermit)) {
      if (_llc.isLZSellersPermit) {
        this.setFieldAnswer('bLZSellersPermit', MAPPING_YESNO.Yes);
      } else {
        this.setFieldAnswer('bLZSellersPermit', MAPPING_YESNO.No);
      }
    } else {
      this.setFieldAnswer('bLZSellersPermit', '');
    }
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.bShowPriceTest)
      && _llc.bShowPriceTest !== 'Null') {
      this.setFieldAnswer('bShowPriceTest', _llc.bShowPriceTest);
      this.setFieldAnswer('bFunnelTest', 'Null');
      this.setFieldAnswer('bShowPriceTest1', 'Null');
    } else if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.bShowPriceTest1)
      && _llc.bShowPriceTest1 !== 'Null') {
      this.setFieldAnswer('bShowPriceTest1', _llc.bShowPriceTest1);
      this.setFieldAnswer('bFunnelTest', 'Null');
      this.setFieldAnswer('bShowPriceTest', 'Null');
    } else {
      this.setFieldAnswer('bFunnelTest', 'Null');
      this.setFieldAnswer('bShowPriceTest', 'Null');
      this.setFieldAnswer('bShowPriceTest1', 'Null');
    }

    if (typeof (s) === 'undefined') {
      s = '';
    }
    // dds LLC
    this.setFieldAnswer('fkUserOrder_hidden', userOrderId.toString());
    this.setFieldAnswer('EIN_Number_hidden', ' ');
    this.setFieldAnswer('Additional_Info_EIN_Filing_MC_hidden', 'No');
    this.setFieldAnswer('Additional_Info_SOS_Filing_MC_hidden', 'No');
    this.setFieldAnswer('Entity_Effective_Date_hidden', ' ');
    this.setFieldAnswer('Future_Effective_Date_MC_hidden', ' ');
    this.setFieldAnswer('ReceiptCode', ' ');
    this.setFieldAnswer('Manual_Filing_SOS_MC_hidden', 'No');
    this.setFieldAnswer('Manual_Filing_EIN_MC_hidden', 'No');
    this.setFieldAnswer('AddProblemManually', MAPPING_CHECK.unchecked);
    this.setFieldAnswer('combined_bapstates', 'TRUE');
    this.setFieldAnswer('combined_showcompliance', 'BAPSTATES');
    // combined_compliance_basic not offered anymore
    // combined_compliance_noBITadvisory not offered anymore
    if (!this.isUndefined(_llc.compliance) && _llc.compliance) {
      this.setFieldAnswer('combined_compliance_full', 'TRUE');
      this.setFieldAnswer('combined_compliance_nonbap_no', 'TRUE');
      this.setFieldAnswer('combined_compliance_essential_any', 'TRUE');
    } else {
      this.setFieldAnswer('combined_compliance_essential_any', '');
      this.setFieldAnswer('combined_compliance_nonbap_no', '');
      this.setFieldAnswer('combined_compliance_soi', s.toString().toLowerCase() === 'california' ? 'TRUE' : '');

      if (s.toString().toLowerCase() === 'alabama' || s.toString().toLowerCase() === 'arkansas' ||
        s.toString().toLowerCase() === 'maryland' || s.toString().toLowerCase() === 'oklahoma') {
        this.setFieldAnswer('combined_compliance_no', 'TRUE');
      }

      if (!this.isUndefined(_llc.lzRA) && _llc.lzRA === MAPPING_YESNO.No) {
        this.setFieldAnswer('combined_compliance_cal', 'TRUE');
      } else {
        this.setFieldAnswer('combined_compliance_cal', '');
      }


      if (!this.isUndefined(s)) {
        if (s.toString().toLowerCase() === 'new jersey') {
          this.setFieldAnswer('combined_compliance_stid', 'NJ');
        } else if (s.toString().toLowerCase() === 'ohio') {
          this.setFieldAnswer('combined_compliance_stid', 'OH');
        }
        if (s.toString().toLowerCase() === 'minnesota') {
          this.setFieldAnswer('combined_compliance_stid', 'MN');
        } else {
          this.setFieldAnswer('combined_compliance_stid', '');
        }

      }

      if (!this.isUndefined(s) && s !== 'arizona' && s !== 'maine' && s !== 'ohio' && s !== 'wyoming') {
        this.setFieldAnswer('combined_compliance_essential_no', 'TRUE');
      }
    }

    if (!this.isUndefined(s) && (s === 'Nebraska' || s === 'Michigan') && !this.isUndefined(_llc.isProfessional) && _llc.isProfessional === true) {
      this.setFieldAnswer('combined_noshowcompliance', 'TRUE');
    }

    if (!this.isUndefined(_llc)) {
      if (!this.isUndefined(_llc.isExpedite) && _llc.isExpedite) {
        this.setFieldAnswer('bExpedite', MAPPING_YESNO.Yes);
      } else {
        this.setFieldAnswer('bExpedite', MAPPING_YESNO.No);
      }
    }

    // Some default values per current questionnaire.
    if (!this.isUndefined(_llc.is3Pay) && _llc.is3Pay) {
      this.setFieldAnswer('b3pay', 'Yes');
    } else {
      this.setFieldAnswer('b3pay', 'No');
    }

    this.setFieldAnswer('b5in5', 'NULL');
    this.setFieldAnswer('b5in5_Price', '');

    this.setFieldAnswer('bPaychex', 'No');
    this.setFieldAnswer('bShowTest2', 'LZ_LLC_Optimization_Experiment');
    this.setFieldAnswer('advisory_offer_MC', 'Yes, keep my business protected with legal help when I need it');

    // customer_phone_number
    if (!this.isUndefined(_llc.customerPhoneNumber) &&
      _llc.customerPhoneNumber.length > 0) {
      this.setFieldAnswer('customer_phone_number', _llc.customerPhoneNumber);
    }

    // customer_email
    if (!this.isUndefined(_llc.customerEmail) &&
      _llc.customerEmail.length > 0) {
      this.setFieldAnswer('customer_email', _llc.customerEmail);
    }

    // mapping formation date
    if (ConstantsService.STATES_REQUIRING_FORMATION_DATE.includes(s)) {
      this.setFieldAnswer('FormationDate', ' ');
    }
    // mapping sos retry
    if (ConstantsService.STATES_REQUIRING_SOS_RETRY.includes(s)) {
      this.setFieldAnswer('RetrySOSFiling', MAPPING_CHECK.unchecked);
    }


    // LLC_type -- professional_MC - Proffessional business
    if (!this.isUndefined(_llc.isProfessionalLLC) &&
      _llc.isProfessionalLLC) {
      this.setFieldAnswer('LLC_type', MAPPING_YESNO.Yes);
    } else {
      this.setFieldAnswer('LLC_type', MAPPING_YESNO.No);
    }
    const fieldObject = this.getFieldAnswer('LLC_type');
    if (fieldObject.fieldValue === MAPPING_YESNO.Yes && s.toString().toLowerCase() === 'colorado') {
      this.setFieldAnswer('Professional_License_Uploaded_MC_hidden', 'No');
    } else if (fieldObject.fieldValue === MAPPING_YESNO.No || s.toString().toLowerCase() !== 'colorado') {
      this.setFieldAnswer('Professional_License_Uploaded_MC_hidden', '');
    }

    if (!this.isUndefined(s) && s.toString().toLowerCase() === 'connecticut') {// businesspurpose is always other
      this.setFieldAnswer('combined_prof_not_CT', '');
    } else {
      this.setFieldAnswer('combined_prof_not_CT', 'TRUE');
    }

    // new_business_MC - New business
    // new_or_convert_business
    this.setFieldAnswer('new_or_convert_business', 'Form a new LLC');

    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.businessStartTime)) {
      if (_llc.businessStartTime === VALUES_NEWBUSINESS.already) {
        this.setFieldAnswer('business_start_MC', MAPPING_NEWBUSINESS.already);
      } else if (_llc.businessStartTime === VALUES_NEWBUSINESS.soon) {
        this.setFieldAnswer('business_start_MC', MAPPING_NEWBUSINESS.soon);
      } else if (_llc.businessStartTime === VALUES_NEWBUSINESS.future) {
        this.setFieldAnswer('business_start_MC', MAPPING_NEWBUSINESS.future);
      }
    }

    // Hari added on 09/03/2019 for pkg price test.
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.packageSelected)) {
      if (_llc.packageSelected === ProductConfigurationIds.economyLLCPackage) {
        this.setFieldAnswer('selectedPkg_MC', MAPPING_SELECTTED_PKG.Economy);
      } else if (_llc.packageSelected === ProductConfigurationIds.standarLLCPackage) {
        this.setFieldAnswer('selectedPkg_MC', MAPPING_SELECTTED_PKG.Standard);
      } else if (_llc.packageSelected === ProductConfigurationIds.expressGoldLLCPackage) {
        this.setFieldAnswer('selectedPkg_MC', MAPPING_SELECTTED_PKG.Gold);
      }
    }

    // hire_employees_MC - hiring employees
    // Hire_employees_ - 27
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.hireEmployees)) {
      if (_llc.hireEmployees === MAPPING_YESNO.Yes.toLowerCase()) {
        this.setFieldAnswer('Hire_employees_', MAPPING_YESNO.Yes);
        if (!this.isUndefined(_llc.numberOfEmployees) && !this.isUndefined(_llc.willhaveAgriculturalEmployees)) {
          if (_llc.willhaveAgriculturalEmployees) {
            this.setFieldAnswer('Nonagricultural_employees', _llc.numberOfEmployees.toString());
          }
        }
      } else {
        this.setFieldAnswer('Hire_employees_', MAPPING_YESNO.No);
      }
    }

    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.naicsCode)) {
      this.setFieldAnswer('NAICS_code', _llc.naicsCode.toString());
    }

    // bBookKeepingOffer
    this.setFieldAnswer('bBookKeepingOffer', MAPPING_YESNO.Yes);


    // CA_FTB_Delay
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.bDelayLLCCA)) {
      if (_llc.bDelayLLCCA === true) {
        this.setFieldAnswer('CA_FTB_Delay', 'Delay Filing');
      } else {
        this.setFieldAnswer('CA_FTB_Delay', 'File ASAP');
      }
    }

    // fiscal_yr_end_month - month(Ex: Decenmber)
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.endFiscalYrMonth)) {
      this.setFieldAnswer('fiscal_yr_end_month', _llc.endFiscalYrMonth);
    }

    // fiscal_yr_end_day - day(Ex: 31)
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.endFiscalYrDay)) {
      this.setFieldAnswer('fiscal_yr_end_day', _llc.endFiscalYrDay);
    }

    // business_address, business_city_state_zip - City, business_state_RO - State, business_zip, business_phone
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.businessAddress)) {
      let fullAddress = _llc.businessAddress.address1;
      if (_llc.businessAddress.address2) {
        fullAddress += ', ' + _llc.businessAddress.address2;
      }
      this.setFieldAnswer('business_address', fullAddress);
      this.setFieldAnswer('business_city_state_zip', _llc.businessAddress.city);
      this.setFieldAnswer('business_county', _llc.businessAddress.county);
      this.setFieldAnswer('business_state', _llc.businessAddress.state);
      this.setFieldAnswer('business_zip', _llc.businessAddress.zipCode);
      // this.setFieldAnswer("business_phone_2", _llc.businessPhone);
    }

    if (!this.isUndefined(_llc.contactPhone)) {
      this.setFieldAnswer('business_phone', _llc.contactPhone);
    }

    if (!this.isUndefined(_llc.businessPhone) && _llc.businessPhone.length > 0) {
      this.setFieldAnswer('business_phone', _llc.businessPhone); // valid for AK, WY and NC
    }

    if (!this.isUndefined(_llc.businessEmail)) {
      this.setFieldAnswer('business_email', _llc.businessEmail);
    }

    // LLC_name - Entity name
    let llcName = '';
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.entityName)) {
      llcName = _llc.entityName;
    }

    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.entityNameDesignator)) {
      llcName = llcName + ' ' + _llc.entityNameDesignator;
    }
    this.setFieldAnswer('LLC_name', llcName);

    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.contactPhone)) {
      this.setFieldAnswer('CONTACT_PHONE', _llc.contactPhone);
    }


    // Primary_Business_Activity - Primary business activity
    this.setFieldAnswer('business_purpose', 'Other');
    this.setFieldAnswer('Primary_Business_Activity', 'Other');
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.businessPurpose)) {
      const bizPurpose = _llc.businessPurpose;
      // if (!this.isUndefined(_llc.businessPurposeDescription)) {
      //   bizPurpose = _llc.businessPurpose + ' - ' + _llc.businessPurposeDescription;
      // }

      this.setFieldAnswer('other_other_activity_detailed', bizPurpose);
    } else {
      this.setFieldAnswer('other_other_activity_detailed', '');
    }


    // insert order details
    if (!this.isUndefined(_llc) && !this.isUndefined(_llc.orderId) && !this.isNullOrEmpty(_llc.orderId)) {
      this.setFieldAnswer('fkOrder_hidden', _llc.orderId.toString());
    }

    // set Activity_Details_EIN_short_hidden and Activity_Details_EIN_long_hidden
    if (!this.isUndefined(_llc) && !this.isUndefined(_llc.businessPurpose) && !this.isNullOrEmpty(_llc.businessPurpose)) {
      this.setFieldAnswer('Activity_Details_EIN_short_hidden', _llc.businessPurpose);
      this.setFieldAnswer('Activity_Details_EIN_long_hidden', _llc.businessPurpose);
    }

    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.businessPurposeDescription)) {
      this.setFieldAnswer('other_other_activity_detailed_desc', _llc.businessPurposeDescription);
    } else {
      this.setFieldAnswer('other_other_activity_detailed_desc', '');
    }

    // Alternative_Name_1 - Entity name alternate1
    let alternateName = '';
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.alternateName)) {
      alternateName = _llc.alternateName;
    }

    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.alternateNameDesignator)) {
      alternateName = alternateName + ' ' + _llc.alternateNameDesignator;
    }
    this.setFieldAnswer('Alternative_Name_1', alternateName);


    // Alternative_Name_2 - Entity name alternate2
    this.setFieldAnswer('Alternative_Name_2', '');

    // Q2 - Business Name Explanation
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.businessNameExplanation)) {
      this.setFieldAnswer('businessNameExplanation', _llc.businessNameExplanation);
    }

    // isBusinessName
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.isBusinessName)) {
      const val = _llc.isBusinessName ? MAPPING_YESNO.Yes : MAPPING_YESNO.No;
      this.setFieldAnswer('isBusinessName', val);
    }

    // Trade_name_name - Trade name
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.tradeName)) {
      if (_llc.tradeName === VALUES_TRADE_NAME.Yes) {
        this.setFieldAnswer('Trade_name', MAPPING_YESNO.Yes);
      } else if (_llc.tradeName === VALUES_TRADE_NAME.NotSure) {
        this.setFieldAnswer('Trade_name', MAPPING_YESNO.No);
      } else if (_llc.tradeName === VALUES_TRADE_NAME.No) {
        this.setFieldAnswer('Trade_name', MAPPING_YESNO.No);
      }
    } else {
      this.setFieldAnswer('Trade_name', MAPPING_YESNO.No);
    }

    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.willAutoDisolve)) {
      const val = _llc.willAutoDisolve ? 'Yes' : 'No';

      this.setFieldAnswer('Dissolve_or_Perpetual_', val);
    }

    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.isFirstLLC)) {
      const val = _llc.isFirstLLC ? MAPPING_YESNO.Yes : MAPPING_YESNO.No;
      this.setFieldAnswer('bFirstLLC', val);
    }

    // managed_owners_MC -- _llc.whoAreManagers
    // number_members - ownerNumber
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.individualOwners) &&
      !this.isUndefined(_llc.businessEntityOwners) &&
      !this.isUndefined(_llc.trustOwners)) {
      this.setOwners(_llc);
      const ownerCount = _llc.owners.length;
      let val = '4 or more owners';
      if (ownerCount === 1) {
        val = '1 owner';
      } else if (ownerCount === 2) {
        val = '2 owners';
      } else if (ownerCount === 3) {
        val = '3 owners';
      }

      this.setFieldAnswer('number_owners_MC', val);
      if (!this.isUndefined(_llc.entityState) &&
        this.isNumberMemberState(_llc.entityState)) {
        if (ownerCount < 10) {
          this.setFieldAnswer('number_members', ownerCount.toString());
        } else {
          this.setFieldAnswer('number_members', '10 or more');
        }
      }

      this.managersIndex = 0;
      if (ownerCount === 1) {
        // owner_title_MC - Owner title
        this.setSingleOwnerManagement(_llc);
      } else {
        this.setMultipleOwnersManagement(_llc);
      }
    }

    if (!this.isUndefined(_llc.managers)) {
      this.setNonOwnerManagers(_llc);
    }

    // registered_agent
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.lzRA)) {
      if (_llc.lzRA === MAPPING_YESNO.Yes) {
        this.setFieldAnswer('registered_agent', MAPPING_REGISTERED_AGENT.Legalzoom);
      } else if (_llc.lzRA === MAPPING_YESNO.No) {
        this.setFieldAnswer('registered_agent', MAPPING_REGISTERED_AGENT.Self);
        if (!this.isUndefined(_llc.raDetails)) {
          if (_llc.raDetails.typeIndicator) {
            this.setFieldAnswer('RA_Type_Indicator', _llc.raDetails.typeIndicator.toString());
            if (_llc.raDetails.typeIndicator === RegisteredAgentType.Individual) {
              this.setFieldAnswer('registered_agent_2', this.getFullName(_llc.raDetails.firstName, _llc.raDetails.middleName, _llc.raDetails.lastName, _llc.raDetails.suffix));
              this.setFieldAnswer('RA_FirstName', _llc.raDetails.firstName);
              this.setFieldAnswer('RA_MiddleName', _llc.raDetails.middleName);
              this.setFieldAnswer('RA_LastName', _llc.raDetails.lastName);
              this.setFieldAnswer('RA_NameSuffix', _llc.raDetails.suffix);
            }
            if (_llc.raDetails.typeIndicator === RegisteredAgentType.BusinessEntity) {
              this.setFieldAnswer('registered_agent_2', _llc.raDetails.fullName);
              this.setFieldAnswer('RA_EntityName', _llc.raDetails.fullName);
            }
          }

          if (ConstantsService.STATES_REQUIRING_CRA_INDICATOR.includes(_llc.entityState)) {
            if (_llc.raDetails.isCRA === true) {
              this.setFieldAnswer('RA_CRA_Indicator', MAPPING_YESNO.Yes);
            } else {
              this.setFieldAnswer('RA_CRA_Indicator', MAPPING_YESNO.No);
            }
          }

          if (_llc.raDetails.raIdNumber != null && _llc.raDetails.raIdNumber !== 'undefined' && _llc.raDetails.raIdNumber !== '') {
            this.setFieldAnswer('RA_ID_Number', _llc.raDetails.raIdNumber);
          } else {
            this.setFieldAnswer('RA_ID_Number', ' ');
          }

          if (_llc.raDetails.address != null) {
            let fullAddress = _llc.raDetails.address.address1;
            if (_llc.raDetails.address.address2) {
              fullAddress += ', ' + _llc.raDetails.address.address2;
            }
            this.setFieldAnswer('registered_agent_3', fullAddress);
            this.setFieldAnswer('registered_agent_4', _llc.raDetails.address.city);
            this.setFieldAnswer('registered_agent_county', this.getReplaceSaint(_llc.raDetails.address.county));
            this.setFieldAnswer('registered_agent_6', _llc.raDetails.address.state);
            this.setFieldAnswer('registered_agent_5', _llc.raDetails.address.zipCode);
            this.setFieldAnswer('registered_agent_email', _llc.raDetails.email);
          }
        }
      }
    }

    // oa_bundle_MC
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.documentsPackage)) {
      if (_llc.documentsPackage === 'OA') {
        this.setFieldAnswer('oa_bundle_MC', MAPPING_OA_BUNDLE.OA_Only);
        _llc.obtainEIN = 'No';
        _llc.bizLicenseOffer = false;
      } else if (_llc.documentsPackage === 'OA_EIN') {
        _llc.obtainEIN = 'Yes';
        this.setFieldAnswer('oa_bundle_MC', MAPPING_OA_BUNDLE.OA_And_EIN);
        _llc.bizLicenseOffer = false;
      } else if (_llc.documentsPackage === 'OA_EIN_LICENCES') {
        _llc.obtainEIN = 'Yes';
        _llc.bizLicenseOffer = true;
        this.setFieldAnswer('oa_bundle_MC', MAPPING_OA_BUNDLE.OA_And_EIN_And_BL);
      } else {
        this.setFieldAnswer('oa_bundle_MC', '');
        _llc.obtainEIN = 'No';
        _llc.bizLicenseOffer = false;
      }
    }
    // biz_license_pkg_CB - Biz license offer
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.bizLicenseOffer)) {
      if (_llc.bizLicenseOffer === true) {
        this.setFieldAnswer('biz_license_pkg_CB', MAPPING_CHECK.checked);
      } else {
        this.setFieldAnswer('biz_license_pkg_CB', MAPPING_CHECK.unchecked);
      }
    }

    // EIN_ --ein_MC -- File EIN
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.obtainEIN)) {


      if (_llc.obtainEIN === VALUES_EIN_CHOICE.Yes) {
        this.setFieldAnswer('EIN_', MAPPING_EIN_CHOICE.Yes);
        this.setFieldAnswer('Manual__Filing_EIN_MC_hidden', '');
        this.setFieldAnswer('ThirdPartyEinFiling', MAPPING_CHECK.unchecked);
        if (!this.isUndefined(_llc) &&
          !this.isUndefined(_llc.individualOwners) && _llc.individualOwners.findIndex(x => x.isIrsContact === true) !== -1) {
          const indIndex = _llc.individualOwners.findIndex(x => x.isIrsContact === true);
          this.setFieldAnswer('Owner_Name_Indicator', 'Individual');
          this.setFieldAnswer('TaxMatterMember_FullName',
            this.getFullName(_llc.individualOwners[indIndex].firstName,
              _llc.individualOwners[indIndex].middleName,
              _llc.individualOwners[indIndex].lastName,
              _llc.individualOwners[indIndex].suffix)
          );
          this.setFieldAnswer('name_of_officer',
            this.getFullName(_llc.individualOwners[indIndex].firstName,
              _llc.individualOwners[indIndex].middleName,
              _llc.individualOwners[indIndex].lastName,
              _llc.individualOwners[indIndex].suffix)
          );
          this.setFieldAnswer('TaxMatterMember_FirstName', _llc.individualOwners[indIndex].firstName.trim());
          this.setFieldAnswer('TaxMatterMember_LastName', _llc.individualOwners[indIndex].lastName.trim());
          this.setFieldAnswer('TaxMatterMember_MiddleName', _llc.individualOwners[indIndex].middleName.trim());
          this.setFieldAnswer('TaxMatterMember_Suffix', _llc.individualOwners[indIndex].suffix.trim());
          this.setFieldAnswer('phone___of_officer', _llc.individualOwners[indIndex].phoneNumber);
          this.setFieldAnswer('SSN_of_officer', this.getSsn(_llc.individualOwners[indIndex].ssnArea, _llc.individualOwners[indIndex].ssnGroup, _llc.individualOwners[indIndex].ssnSerial));
        }
        if (!this.isUndefined(_llc) &&
          !this.isUndefined(_llc.businessEntityOwners) && _llc.businessEntityOwners.findIndex(x => x.isIrsContact === true) !== -1) {
          const indIndex = _llc.businessEntityOwners.findIndex(x => x.isIrsContact === true);
          this.setFieldAnswer('Owner_Name_Indicator', 'BusinessEntity');
          this.setFieldAnswer('TaxMatterMember_Entity', _llc.businessEntityOwners[indIndex].fullName.trim());
          this.setFieldAnswer(
            'TaxMatterMember_FullName',
            this.getFullName(_llc.businessEntityOwners[indIndex].contact.firstName,
              _llc.businessEntityOwners[indIndex].contact.middleName,
              _llc.businessEntityOwners[indIndex].contact.lastName,
              _llc.businessEntityOwners[indIndex].contact.suffix)
          );
          this.setFieldAnswer('name_of_officer',
            this.getFullName(_llc.businessEntityOwners[indIndex].contact.firstName,
              _llc.businessEntityOwners[indIndex].contact.middleName,
              _llc.businessEntityOwners[indIndex].contact.lastName,
              _llc.businessEntityOwners[indIndex].contact.suffix)
          );
          this.setFieldAnswer('TaxMatterMember_FirstName', _llc.businessEntityOwners[indIndex].contact.firstName.trim());
          this.setFieldAnswer('TaxMatterMember_LastName', _llc.businessEntityOwners[indIndex].contact.lastName.trim());
          this.setFieldAnswer('TaxMatterMember_MiddleName', _llc.businessEntityOwners[indIndex].contact.middleName.trim());
          this.setFieldAnswer('TaxMatterMember_Suffix', _llc.businessEntityOwners[indIndex].contact.suffix.trim());
          this.setFieldAnswer('phone___of_officer', _llc.businessEntityOwners[indIndex].contact.phoneNumber);
          this.setFieldAnswer(
            'SSN_of_officer',
            this.getSsn(_llc.businessEntityOwners[indIndex].contact.ssnArea,
              _llc.businessEntityOwners[indIndex].contact.ssnGroup,
              _llc.businessEntityOwners[indIndex].contact.ssnSerial)
          );
        }
        if (!this.isUndefined(_llc) &&
          !this.isUndefined(_llc.trustOwners) && _llc.trustOwners.findIndex(x => x.isIrsContact === true) !== -1) {
          const indIndex = _llc.trustOwners.findIndex(x => x.isIrsContact === true);
          this.setFieldAnswer('Owner_Name_Indicator', 'Trust');
          this.setFieldAnswer('TaxMatterMember_Trust', _llc.trustOwners[indIndex].fullName.trim());
          this.setFieldAnswer(
            'TaxMatterMember_FullName',
            this.getFullName(_llc.trustOwners[indIndex].trustGrantorContact.firstName,
              _llc.trustOwners[indIndex].trustGrantorContact.middleName,
              _llc.trustOwners[indIndex].trustGrantorContact.lastName,
              _llc.trustOwners[indIndex].trustGrantorContact.suffix)
          );
          this.setFieldAnswer(
            'name_of_officer',
            this.getFullName(_llc.trustOwners[indIndex].trustGrantorContact.firstName,
              _llc.trustOwners[indIndex].trustGrantorContact.middleName,
              _llc.trustOwners[indIndex].trustGrantorContact.lastName,
              _llc.trustOwners[indIndex].trustGrantorContact.suffix)
          );
          this.setFieldAnswer('TaxMatterMember_FirstName', _llc.trustOwners[indIndex].trustGrantorContact.firstName.trim());
          this.setFieldAnswer('TaxMatterMember_LastName', _llc.trustOwners[indIndex].trustGrantorContact.lastName.trim());
          this.setFieldAnswer('TaxMatterMember_MiddleName', _llc.trustOwners[indIndex].trustGrantorContact.middleName.trim());
          this.setFieldAnswer('TaxMatterMember_Suffix', _llc.trustOwners[indIndex].trustGrantorContact.suffix.trim());
          this.setFieldAnswer('phone___of_officer', _llc.trustOwners[indIndex].trustGrantorContact.phoneNumber);
          this.setFieldAnswer(
            'SSN_of_officer',
            this.getSsn(_llc.trustOwners[indIndex].trustGrantorContact.ssnArea,
              _llc.trustOwners[indIndex].trustGrantorContact.ssnGroup,
              _llc.trustOwners[indIndex].trustGrantorContact.ssnSerial)
          );
        }
      } else if (_llc.obtainEIN === VALUES_EIN_CHOICE.No) {
        this.setFieldAnswer('EIN_', MAPPING_EIN_CHOICE.No);
      }

      if (!this.isUndefined(_llc.numberOfAgriculturalEmployees)) {
        this.setFieldAnswer('Agricultural_employees', _llc.numberOfAgriculturalEmployees.toString());
        if (_llc.numberOfAgriculturalEmployees > 0) {
          this.setFieldAnswer('Hire_employees_', MAPPING_YESNO.Yes);
        }
      }


      if (!this.isUndefined(_llc.numberOfEmployees)) {
        this.setFieldAnswer('Nonagricultural_employees', _llc.numberOfEmployees.toString());
        if (_llc.numberOfEmployees) {
          this.setFieldAnswer('Hire_employees_', MAPPING_YESNO.Yes);
        }
      }

      if (!this.isUndefined(_llc.numberOfHouseholdEmployees)) {
        this.setFieldAnswer('Household_employees', _llc.numberOfHouseholdEmployees.toString());
        if (_llc.numberOfHouseholdEmployees) {
          this.setFieldAnswer('Hire_employees_', MAPPING_YESNO.Yes);
        }
      }

      if (!this.isUndefined(_llc.startEmpPayDate)) {
        this.setFieldAnswer('Date_Wages_Paid', this.updateDateValueToCorrectFormat(_llc.startEmpPayDate));
      }

      if (!this.isUndefined(_llc.dissolveDate)) {
        this.setFieldAnswer('Date_of_Dissolution', this.updateDateValueToCorrectFormat(_llc.dissolveDate));
      }

      if (!this.isUndefined(_llc.willPayEmpOverThreshold)) {
        this.setFieldAnswer('liability_more_than_4k', _llc.willPayEmpOverThreshold ? MAPPING_YESNO.Yes : MAPPING_YESNO.No);
      }

      if (!this.isUndefined(_llc.alreadyHaveEIN)) {
        this.setFieldAnswer('Previous_EIN', _llc.alreadyHaveEIN ? MAPPING_YESNO.Yes : MAPPING_YESNO.No);
      }

      if (!this.isUndefined(_llc.existingEinNumber)) {
        this.setFieldAnswer('Previous_EIN_list_number', _llc.existingEinNumber);
      }

      if (!this.isUndefined(_llc.doingBusinessAs)) {
        this.setFieldAnswer('Trade_name', _llc.doingBusinessAs ? MAPPING_YESNO.Yes : MAPPING_YESNO.No);
      }

      if (!this.isUndefined(_llc.dbaName)) {
        this.setFieldAnswer('Trade_name_name', _llc.dbaName);
      }

      this.setFieldAnswer('vehicle_taxgross_weight', _llc.willOwnTransportVehicle ? MAPPING_YESNO.Yes : MAPPING_YESNO.No);

      this.setFieldAnswer('gambling_wagering', _llc.willGamble ? MAPPING_YESNO.Yes : MAPPING_YESNO.No);

      this.setFieldAnswer('manufacture_sell_alcohol', _llc.willSellAlcohol ? MAPPING_YESNO.Yes : MAPPING_YESNO.No);

      this.setFieldAnswer('file_form720', _llc.willSellExciseTaxGoods ? MAPPING_YESNO.Yes : MAPPING_YESNO.No);

    }

    // EIN_ --ein_MC -- File EIN
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.obtainEIN)) {
      if (_llc.obtainEIN === VALUES_EIN_CHOICE.Yes) {
        this.setFieldAnswer('EIN_', MAPPING_EIN_CHOICE.Yes);
      } else if (_llc.obtainEIN === VALUES_EIN_CHOICE.YesForm) {
        this.setFieldAnswer('EIN_', MAPPING_EIN_CHOICE.YesForm);
      } else if (_llc.obtainEIN === VALUES_EIN_CHOICE.No) {
        this.setFieldAnswer('EIN_', MAPPING_EIN_CHOICE.No);
      }
    } else {
      this.setFieldAnswer('EIN_', MAPPING_EIN_CHOICE.No);
    }

    // bStateTaxId - State Tax Registration
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.isStateTaxReg)) {
      if (_llc.isStateTaxReg === true) {
        this.setFieldAnswer('bStateTaxId', MAPPING_YESNO.Yes);
      } else {
        this.setFieldAnswer('bStateTaxId', MAPPING_YESNO.No);
      }
    } else {
      this.setFieldAnswer('bStateTaxId', MAPPING_YESNO.No);
    }

    // compliance_BIT_CB - Full service CP
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.totalCompliance)) {
      if (_llc.totalCompliance === true) {
        this.setFieldAnswer('compliance_BIT_CB', MAPPING_CHECK.checked);
        this.setFieldAnswer('compliance_no_CB', MAPPING_CHECK.unchecked);
      } else {
        this.setFieldAnswer('compliance_BIT_CB', MAPPING_CHECK.unchecked);
        this.setFieldAnswer('compliance_no_CB', MAPPING_CHECK.checked);
      }
    }

    // bTaxAdvise - BAP Tax
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.taxAdvice)) {
      if (_llc.taxAdvice === true) {
        this.setFieldAnswer('bTaxAdvise', MAPPING_YESNO.Yes);
      } else {
        this.setFieldAnswer('bTaxAdvise', MAPPING_YESNO.No);
      }
    } else {
      this.setFieldAnswer('bTaxAdvise', MAPPING_YESNO.No);
    }

    // bLegalAdvise - BAP Legal
    if (_llc.packageSelected === ProductConfigurationIds.tier2LLCPackage || _llc.packageSelected === ProductConfigurationIds.tier3LLCPackage) {
      this.setFieldAnswer('bLegalAdvise', MAPPING_YESNO.Yes);
    } else if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.legalAdvice)) {
      if (_llc.legalAdvice === true) {
        this.setFieldAnswer('bLegalAdvise', MAPPING_YESNO.Yes);
      } else {
        this.setFieldAnswer('bLegalAdvise', MAPPING_YESNO.No);
      }
    } else {
      this.setFieldAnswer('bLegalAdvise', MAPPING_YESNO.No);
    }

    // Hari added on 06/06/2019 for Q2 Init R2 project
    if (!this.isUndefined(_llc) && _llc.packageSelected === ProductConfigurationIds.tier3LLCPackage) {
      this.setFieldAnswer('bCustomLogoDesign', MAPPING_YESNO.Yes);
      this.setFieldAnswer('bSmartBusinessPlan', MAPPING_YESNO.Yes);
    } else {
      this.setFieldAnswer('bCustomLogoDesign', MAPPING_YESNO.No);
      this.setFieldAnswer('bSmartBusinessPlan', MAPPING_YESNO.No);
    }

    // bankofamerica_offer_MC - BOA offer
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.bofa)) {
      if (_llc.bofa === true) {
        this.setFieldAnswer('bankofamerica_offer_MC', MAPPING_BOAOFFER.Yes);
      } else {
        this.setFieldAnswer('bankofamerica_offer_MC', MAPPING_BOAOFFER.No);
      }
    }

    // Webcom_consultation_MC - web.com offer defaulting NO
    this.setFieldAnswer('Webcom_consultation_MC', MAPPING_WEBCOMOFFER.No);

    // CTN_consult_MC - CTN consult accountant1800
    this.setFieldAnswer('CTN_consult_MC', MAPPING_CTNCONSULT.No);

    // adp_offer_MC
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.adp)) {
      if (_llc.adp === true) {
        this.setFieldAnswer('adp_offer_MC', MAPPING_ADP_OFFER.Yes);
      } else {
        this.setFieldAnswer('adp_offer_MC', MAPPING_ADP_OFFER.No);
      }
    }

    // mylo_offer_MC-- MYLO Offer
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.mylo)) {
      if (_llc.mylo === true) {
        this.setFieldAnswer('mylo_offer_MC', MAPPING_YESNO.Yes);
      } else {
        this.setFieldAnswer('mylo_offer_MC', MAPPING_YESNO.No);
      }
    }

    // CEP_consult_MC - CEP consult no this could be empty
    this.setFieldAnswer('CEP_consult_MC', MAPPING_CEPCONSULT.No);

    // setting defaults
    this.setFieldAnswer('LLC_name_RO', '');
    this.setFieldAnswer('Owner_Individual_Required', !this.isUndefined(_llc) && !this.isUndefined(_llc.individualOwners) && _llc.individualOwners.length > 0 ? MAPPING_YESNO.Yes : MAPPING_YESNO.No);
    this.setFieldAnswer('Owner_BusinessEntity_Required', !this.isUndefined(_llc) && !this.isUndefined(_llc.businessEntityOwners) && _llc.businessEntityOwners.length > 0 ? MAPPING_YESNO.Yes : MAPPING_YESNO.No);
    this.setFieldAnswer('Owner_Trust_Required', !this.isUndefined(_llc) && !this.isUndefined(_llc.trustOwners) && _llc.trustOwners.length > 0 ? MAPPING_YESNO.Yes : MAPPING_YESNO.No);
    if (this.groupAnswerArray.length > 0) {
      this.mapGroupFieldAnswer(_llc);
    }
    this._mapped.groupAnswerCollections = this.groupAnswerArray;
    return this._mapped;
  }

  mapGroupFieldAnswer(llc: LLC) {
    this.groupAnswerArray.forEach((group) => {
      if (this._groupFieldAnswerLookup.indexOf(group.groupName) === -1) {
        this._groupFieldAnswerLookup.push(group.groupName);
      }
    });
    this._groupFieldAnswerLookup.forEach((field) => {
      switch (field) {
        case 'Owner_Individual':
          this.setFieldAnswer(field, llc.individualOwners.length.toString());
          break;
        case 'Owner_BusinessEntity':
          this.setFieldAnswer(field, llc.businessEntityOwners.length.toString());
          break;
        case 'Owner_Trust':
          this.setFieldAnswer(field, llc.trustOwners.length.toString());
          break;
        case 'owner_info':
          this.setFieldAnswer(field, llc.owners.length.toString());
          break;
        case 'owner_percent_info':
          this.setFieldAnswer(field, llc.owners.length.toString());
          break;
        case 'owner_units_info':
          this.setFieldAnswer(field, llc.owners.length.toString());
          break;
        case 'stockholders':
          this.setFieldAnswer(field, llc.owners.length.toString());
          break;
        case 'stockholders2':
          this.setFieldAnswer(field, llc.owners.length.toString());
          break;
        case 'managers2':
          this.setFieldAnswer(field, this.getManagerCount(llc).toString());
          break;
      }
    });
  }

  getManagerCount(llc: LLC) {
    let otherManagers = 0;
    let individualOwnerAsManagers = 0;
    if (!this.isUndefined(llc.managers)) {
      otherManagers = llc.managers.length;
    }
    if (llc.whoAreManagers === VALUES_MANAGEDOWNERS.ownersAndNonOwners && llc.individualOwners.length === 1) {
      individualOwnerAsManagers = llc.individualOwners.length;
    } else if (llc.whoAreManagers === VALUES_MANAGEDOWNERS.nonOwnersOnly) {
      individualOwnerAsManagers = llc.individualOwners.filter(x => x.ownerIsManager === true).length;
    }
    return otherManagers + individualOwnerAsManagers;
  }

  isNumberMemberState(stateVal) {
    if (stateVal.toString().toLowerCase() === 'arizona'
      || stateVal.toString().toLowerCase() === 'california'
      || stateVal.toString().toLowerCase() === 'idaho'
      || stateVal.toString().toLowerCase() === 'louisiana'
      || stateVal.toString().toLowerCase() === 'nevada'
      || stateVal.toString().toLowerCase() === 'new mexico'
      || stateVal.toString().toLowerCase() === 'texas'
      || stateVal.toString().toLowerCase() === 'washington'
      || stateVal.toString().toLowerCase() === 'wisconsin') {
      return true;
    } else {
      return false;
    }
  }

  // tslint:disable-next-line: variable-name
  setSingleOwnerManagement(_llc: LLC) {
    let owner: Contact = null;
    let fieldName = null;

    if (_llc.individualOwners.length === 1) {
      owner = _llc.individualOwners[0];
      fieldName = 'Owner_Individual';
    } else if (_llc.businessEntityOwners.length === 1) {
      owner = _llc.businessEntityOwners[0];
      fieldName = 'Owner_BusinessEntity';
    } else if (_llc.trustOwners.length === 1) {
      owner = _llc.trustOwners[0];
      fieldName = 'Owner_Trust';
    }

    if (!owner) {
      return;
    }

    this.setFieldAnswer('owner_name', owner.fullName);
    this.setFieldAnswer('owner_title_MC', owner.title);
    this.setFieldAnswer('title_of_officer', owner.title);
    this.setFieldAnswer('percentage_or_units', 'Percentage');
    this.setFieldAnswer('transfer_of_LLC_units', 'Ownership interests are freely transferable.');

    let ownerIsManager = false;
    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.whoAreManagers)) {
      if (_llc.whoAreManagers === VALUES_MANAGEDOWNER.solelyByOwner) {
        this.setFieldAnswer('managed_owner_MC', MAPPING_MANAGEDOWNER.solelyByOwner);
        this.setFieldAnswer('Management_of_LLC', MAPPING_SINGLEOWNER_MANAGEMENTLLC.solelyByOwner);
        ownerIsManager = false; // doesnt need to map
      } else if (_llc.whoAreManagers === VALUES_MANAGEDOWNERS.ownersAndNonOwners) {
        this.setFieldAnswer('managed_owner_MC', MAPPING_MANAGEDOWNER.ownersAndNonOwners);
        this.setFieldAnswer('Management_of_LLC', MAPPING_SINGLEOWNER_MANAGEMENTLLC.ownersAndNonOwners);
        ownerIsManager = true;
      } else if (_llc.whoAreManagers === VALUES_MANAGEDOWNERS.nonOwnersOnly) {
        this.setFieldAnswer('managed_owner_MC', MAPPING_MANAGEDOWNER.nonOwnersOnly);
        this.setFieldAnswer('Management_of_LLC', MAPPING_SINGLEOWNER_MANAGEMENTLLC.nonOwnersOnly);
        ownerIsManager = false;
      }
    }
    this.setGroupAnswer('stockholders', 'stockholders_capital_1', '');
    this.setGroupAnswer('stockholders', 'stockholders_Name_1', owner.fullName);
    this.setGroupAnswer('stockholders', 'stockholders_Percentage_1', '100%');


    if (fieldName === 'Owner_Individual') {
      this.setGroupAnswer(fieldName, fieldName + '_FullName_1', owner.fullName);
      this.setGroupAnswer(fieldName, fieldName + '_FirstName_1', owner.firstName);
      this.setGroupAnswer(fieldName, fieldName + '_MiddleName_1', owner.middleName);
      this.setGroupAnswer(fieldName, fieldName + '_LastName_1', owner.lastName);
      this.setGroupAnswer(fieldName, fieldName + '_Suffix_1', owner.suffix);
    } else if (fieldName === 'Owner_BusinessEntity' || fieldName === 'Owner_Trust') {
      this.setGroupAnswer(fieldName, fieldName + '_Name_1', owner.fullName);
    }

    this.setGroupAnswer('owner_percent_info', 'owner_percent_info_name_1', owner.fullName);
    this.setGroupAnswer('owner_percent_info', 'owner_percent_info_percentage_1', '100%');
    if (ownerIsManager) {
      this.managersIndex = this.managersIndex + 1;
      this.setGroupAnswer('managers2', 'managers2_Name_' + this.managersIndex, owner.fullName);
    }
  }

  // tslint:disable-next-line: variable-name
  setMultipleOwnersManagement(_llc: LLC) {
    if (!this.isUndefined(_llc.ownershipDivision)) {
      this.setFieldAnswer('percentage_or_units', _llc.ownershipDivision);
    }

    for (let i = 0; i < _llc.individualOwners.length; i++) {
      const cellIndex = i + 1;
      this.setGroupAnswer('Owner_Individual', 'Owner_Individual_FullName_' + cellIndex, _llc.individualOwners[i].fullName);
      this.setGroupAnswer('Owner_Individual', 'Owner_Individual_FirstName_' + cellIndex, _llc.individualOwners[i].firstName);
      this.setGroupAnswer('Owner_Individual', 'Owner_Individual_MiddleName_' + cellIndex, _llc.individualOwners[i].middleName);
      this.setGroupAnswer('Owner_Individual', 'Owner_Individual_LastName_' + cellIndex, _llc.individualOwners[i].lastName);
      this.setGroupAnswer('Owner_Individual', 'Owner_Individual_Suffix_' + cellIndex, _llc.individualOwners[i].suffix);
    }

    for (let i = 0; i < _llc.businessEntityOwners.length; i++) {
      const cellIndex = i + 1;
      this.setGroupAnswer('Owner_BusinessEntity', 'Owner_BusinessEntity_Name_' + cellIndex, _llc.businessEntityOwners[i].fullName);
    }
    for (let i = 0; i < _llc.trustOwners.length; i++) {
      const cellIndex = i + 1;
      this.setGroupAnswer('Owner_Trust', 'Owner_Trust_Name_' + cellIndex, _llc.trustOwners[i].fullName);
    }

    let cellName = '';
    for (let i = 0; i < _llc.owners.length; i++) {
      const cellIndex = i + 1;
      // owner_info_name_
      cellName = 'owner_info_name_' + cellIndex;
      this.setGroupAnswer('owner_info', cellName, _llc.owners[i].fullName);
      // owner_info_title_MC_
      cellName = 'owner_info_title_MC_' + cellIndex;
      this.setGroupAnswer('owner_info', cellName, '');
      // owner_info_title_other_
      cellName = 'owner_info_title_other_' + cellIndex;
      this.setGroupAnswer('owner_info', cellName, '');

      if (!this.isUndefined(_llc) &&
        !this.isUndefined(_llc.ownershipDivision)) {
        if (_llc.ownershipDivision === 'percentage') {
          // owner_percent_info_name_
          cellName = 'owner_percent_info_name_' + cellIndex;
          this.setGroupAnswer('owner_percent_info', cellName, _llc.owners[i].fullName);
          // owner_percent_info_percentage_
          cellName = 'owner_percent_info_percentage_' + cellIndex;
          this.setGroupAnswer('owner_percent_info', cellName, _llc.owners[i].ownershipQty + '%');

          // stockholders_capital_
          cellName = 'stockholders_capital_' + cellIndex;
          this.setGroupAnswer('stockholders', cellName, '');
          // stockholders_Name_
          cellName = 'stockholders_Name_' + cellIndex;
          this.setGroupAnswer('stockholders', cellName, _llc.owners[i].fullName);
          // stockholders_Percentage_
          cellName = 'stockholders_Percentage_' + cellIndex;
          this.setGroupAnswer('stockholders', cellName, _llc.owners[i].ownershipQty + '%');

        } else {
          cellName = 'owner_units_info_bogus_' + cellIndex;
          this.setGroupAnswer('owner_units_info', cellName, '');
          // owner_unit_info_unit_
          cellName = 'owner_units_info_name_' + cellIndex;
          this.setGroupAnswer('owner_units_info', cellName, _llc.owners[i].fullName);

          cellName = 'owner_units_info_units_' + cellIndex;
          this.setGroupAnswer('owner_units_info', cellName, _llc.owners[i].ownershipQty);

          // stockholders_capital_
          cellName = 'stockholders2_Capital_' + cellIndex;
          this.setGroupAnswer('stockholders2', cellName, '');
          // stockholders_Name_
          cellName = 'stockholders2_Name_' + cellIndex;
          this.setGroupAnswer('stockholders2', cellName, _llc.owners[i].fullName);
          // stockholders_Percentage_
          cellName = 'stockholders2_Units_' + cellIndex;
          this.setGroupAnswer('stockholders2', cellName, _llc.owners[i].ownershipQty);
        }
      }
    }

    if (_llc.owners.length === 2) {
      this.set2OwnersFields(_llc);
    }

    let otherManagersOnly = false;
    if (_llc.individualOwners.length === 1) {
      if (!this.isUndefined(_llc) && !this.isUndefined(_llc.whoAreManagers)) {
        if (_llc.whoAreManagers === VALUES_MANAGEDOWNER.solelyByOwner) {
          this.setFieldAnswer('managed_owner_MC', MAPPING_MANAGEDOWNER.solelyByOwner);
          this.setFieldAnswer('Management_of_LLC', MAPPING_SINGLEOWNER_MANAGEMENTLLC.solelyByOwner);
        } else if (_llc.whoAreManagers === VALUES_MANAGEDOWNERS.ownersAndNonOwners) {
          this.setFieldAnswer('managed_owner_MC', MAPPING_MANAGEDOWNER.ownersAndNonOwners);
          this.setFieldAnswer('Management_of_LLC', MAPPING_SINGLEOWNER_MANAGEMENTLLC.ownersAndNonOwners);
        } else if (_llc.whoAreManagers === VALUES_MANAGEDOWNERS.nonOwnersOnly) {
          this.setFieldAnswer('managed_owner_MC', MAPPING_MANAGEDOWNER.nonOwnersOnly);
          this.setFieldAnswer('Management_of_LLC', MAPPING_SINGLEOWNER_MANAGEMENTLLC.nonOwnersOnly);
        }
      }
    } else {
      if (_llc.whoAreManagers === VALUES_MANAGEDOWNERS.allOwners) {
        this.setFieldAnswer('managed_owners_MC', MAPPING_MANAGEDOWNERS.allOwners);
        this.setFieldAnswer('Management_of_LLC', MAPPING_MULTIPLEOWNERS_MANAGEMENTLLC.allOwners);
      } else if (_llc.whoAreManagers === VALUES_MANAGEDOWNERS.someOwners) {
        this.setFieldAnswer('managed_owners_MC', MAPPING_MANAGEDOWNERS.someOwners);
        this.setFieldAnswer('Management_of_LLC', MAPPING_MULTIPLEOWNERS_MANAGEMENTLLC.someOwners);
      } else if (_llc.whoAreManagers === VALUES_MANAGEDOWNERS.ownersAndNonOwners) {
        this.setFieldAnswer('managed_owners_MC', MAPPING_MANAGEDOWNERS.ownersAndNonOwners);
        this.setFieldAnswer('Management_of_LLC', MAPPING_MULTIPLEOWNERS_MANAGEMENTLLC.ownersAndNonOwners);
      } else if (_llc.whoAreManagers === VALUES_MANAGEDOWNERS.nonOwnersOnly) {
        this.setFieldAnswer('managed_owners_MC', MAPPING_MANAGEDOWNERS.nonOwnersOnly);
        this.setFieldAnswer('Management_of_LLC', MAPPING_MULTIPLEOWNERS_MANAGEMENTLLC.nonOwnersOnly);
        otherManagersOnly = true;
      }
    }

    if (!this.isUndefined(_llc) &&
      !this.isUndefined(_llc.ruleRegardingOwnership)) {


      if (_llc.ruleRegardingOwnership === VALUES_CONSENT.noConsent) {
        this.setFieldAnswer('transfer_of_LLC_units', MAPPING_CONSENT.noConsent);
      } else if (_llc.ruleRegardingOwnership === VALUES_CONSENT.majorityConsent) {
        this.setFieldAnswer('transfer_of_LLC_units', MAPPING_CONSENT.majorityConsent);
      } else if (_llc.ruleRegardingOwnership === VALUES_CONSENT.allOwnersConsent) {
        this.setFieldAnswer('transfer_of_LLC_units', MAPPING_CONSENT.allOwnersConsent);
      }
    }


    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < _llc.owners.length; i++) {
      if (_llc.owners[i].ownerIsManager && !otherManagersOnly) {

        this.managersIndex = this.managersIndex + 1;
        // managers2_Name_
        cellName = 'managers2_Name_' + this.managersIndex;
        this.setGroupAnswer('managers2', cellName, _llc.owners[i].fullName);
      }
    }

  }

  // tslint:disable-next-line: variable-name
  set2OwnersFields(_llc: LLC) {
    if (!this.isUndefined(_llc.areOwnersMarried) && _llc.areOwnersMarried) {
      this.setFieldAnswer('members_huswife', 'Yes');
    } else {
      this.setFieldAnswer('members_huswife', 'No');
    }

    if (!this.isUndefined(_llc.classification)) {
      if (_llc.classification === 'single') {
        this.setFieldAnswer('huswife_management', 'Sole Proprietorship');
      } else {
        this.setFieldAnswer('huswife_management', 'Partnership');
      }
    }
  }

  // tslint:disable-next-line: variable-name
  setNonOwnerManagers(_llc: LLC) {
    let cellName = '';

    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < _llc.managers.length; i++) {
      this.managersIndex = this.managersIndex + 1;
      // owner_info_name_
      cellName = 'managers2_Name_' + this.managersIndex;
      this.setGroupAnswer('managers2', cellName, _llc.managers[i].fullName);
    }

  }

  // tslint:disable-next-line: variable-name
  setOwners(_llc) {
    _llc.owners = [];
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < _llc.individualOwners.length; i++) {
      _llc.individualOwners[i].fullName = this.getFullName(_llc.individualOwners[i].firstName, _llc.individualOwners[i].middleName, _llc.individualOwners[i].lastName, _llc.individualOwners[i].suffix);
    }
    _llc.owners = _llc.owners.concat(_llc.individualOwners);
    _llc.owners = _llc.owners.concat(_llc.businessEntityOwners);
    _llc.owners = _llc.owners.concat(_llc.trustOwners);
  }

  getFullName(firstName: string, middleName: string, lastName: string, suffix: string) {
    let fullName = '';
    if (firstName.trim()) {
      fullName = firstName.trim();
    }
    if (middleName && middleName.trim()) {
      fullName += ' ' + middleName.trim();
    }
    if (lastName.trim()) {
      fullName += ' ' + lastName.trim();
    }
    if (suffix && suffix.trim()) {
      fullName += ' ' + suffix.trim();
    }
    return fullName.trim();
  }

  getSsn(ssnArea: string, ssnGroup: string, ssnSerial: string) {
    let ssn = '';

    if (ssnArea.trim()) {
      ssn = ssnArea.trim();
    }
    if (ssnGroup.trim()) {
      ssn += '-' + ssnGroup.trim();
    }
    if (ssnSerial.trim()) {
      ssn += '-' + ssnSerial.trim();
    }
    return ssn.trim();
  }

  getEin(einLocation: string, einNumber: string) {
    let ein = '';

    if (einLocation.trim()) {
      ein = einLocation.trim();
    }
    if (einNumber.trim()) {
      ein += '-' + einNumber.trim();
    }
    return ein;
  }

}


